
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getAvailableTimeSlots } from '@/services/bookingService';

export interface TimeSlot {
  time: string;
  isAvailable: boolean;
}

export interface Availability {
  day: string;
  startTime: string;
  endTime: string;
}

export const useAvailability = (escortId: string | undefined) => {
  const [availabilities, setAvailabilities] = useState<Availability[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch availabilities for the escort
  const fetchAvailability = useCallback(async () => {
    if (!escortId) return;
    
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('escort_availability')
        .select('*')
        .eq('escort_id', escortId);
        
      if (error) throw error;
      
      if (data) {
        const formattedAvailabilities = data.map(item => ({
          day: item.day,
          startTime: item.start_time,
          endTime: item.end_time
        }));
        
        setAvailabilities(formattedAvailabilities);
      }
    } catch (err: any) {
      console.error('Error fetching availability:', err);
      setError(err.message || 'Failed to load availability');
    } finally {
      setIsLoading(false);
    }
  }, [escortId]);

  // Memoize the getAvailableTimeSlots function
  const getTimeSlots = useCallback(async (date: Date, duration: number = 60) => {
    return await getAvailableTimeSlots(escortId, date, duration);
  }, [escortId]);

  // Fetch availabilities when the component loads
  useEffect(() => {
    fetchAvailability();
  }, [escortId, fetchAvailability]);

  return {
    availabilities,
    isLoading,
    error,
    getAvailableTimeSlots: getTimeSlots,
    fetchAvailability
  };
};
